import React, { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send, Check, Palmtree } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { RESTAURANT, TIME_SLOTS } from "../data";
import { useSettings } from "../SiteSettings";
import { api } from "../lib/api";
import Reveal from "./Reveal";
import OliveBranch from "./OliveBranch";

const initialForm = { name: "", email: "", phone: "", date: "", time: "", guests: "2", message: "" };

export const Contact = () => {
  const { t, lang } = useLang();
  const settings = useSettings();
  const R = { ...RESTAURANT, ...(settings.contact || {}) };
  const vac = settings.vacation || {};
  const vacActive = !!vac.active;
  const vacMsg = (lang === "en" ? vac.message_en : vac.message_de) ||
    (lang === "en" ? "We're currently on holiday and cannot accept reservations." : "Wir sind aktuell im Urlaub und können derzeit keine Reservierungen annehmen.");
  const whatsappDigits = (R.whatsapp || "").replace(/\D/g, "") || RESTAURANT.whatsappDigits;
  const mapsQuery = R.street && R.city ? `${R.street}, ${R.city}` : RESTAURANT.mapsQuery;
  const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`;
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const f = t.contact.form;
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reservations", { ...form, guests: parseInt(form.guests, 10) });
      setDone(true);
      toast.success(f.success);
      setForm(initialForm);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery)}`;
  const waUrl = `https://wa.me/${whatsappDigits}`;

  const contactBtns = [
    { icon: Phone, label: t.contact.call, href: `tel:${R.phoneIntl}`, testid: "contact-call" },
    { icon: Mail, label: t.contact.email, href: `mailto:${R.email}`, testid: "contact-email" },
    { icon: MessageCircle, label: t.contact.whatsapp, href: waUrl, testid: "contact-whatsapp" },
    { icon: MapPin, label: t.contact.directions, href: mapsUrl, testid: "contact-directions" },
  ];

  const inputCls =
    "w-full bg-qs-bg border border-qs-border rounded-lg px-4 py-3 font-outfit text-qs-text placeholder:text-qs-text2/60 focus:outline-none focus:border-qs-gold transition-colors";

  return (
    <section id="contact" className="py-24 md:py-32 px-5 md:px-10 bg-qs-bg">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center flex flex-col items-center mb-14">
          <p className="font-outfit text-qs-red tracking-[0.3em] uppercase text-xs mb-4">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl text-qs-text">
            {t.contact.title}
          </h2>
          <OliveBranch className="w-24 text-qs-gold my-5" />
          <p className="font-outfit text-qs-text2 max-w-xl">{t.contact.subtitle}</p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Left: info + map */}
          <Reveal>
            <div className="bg-qs-bg2 p-7 md:p-9">
              <h3 className="font-cormorant text-2xl text-qs-text mb-1">{R.name}</h3>
              <div className="gold-divider my-4" />

              <div className="space-y-4 font-outfit">
                <div className="flex items-start gap-3">
                  <MapPin className="text-qs-gold mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-qs-text2">{t.contact.address}</p>
                    <p className="text-qs-text">{R.street}</p>
                    <p className="text-qs-text">{R.city}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="text-qs-gold mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-qs-text2">{t.contact.phone}</p>
                    <a href={`tel:${R.phoneIntl}`} className="text-qs-text hover:text-qs-red transition-colors">
                      {R.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="text-qs-gold mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-qs-text2">E-Mail</p>
                    <a href={`mailto:${R.email}`} className="text-qs-text hover:text-qs-red transition-colors break-all">
                      {R.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-7">
                {contactBtns.map((b) => (
                  <a
                    key={b.testid}
                    data-testid={b.testid}
                    href={b.href}
                    target={b.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 border border-qs-border bg-qs-bg rounded-full py-3 text-xs font-semibold tracking-wide uppercase text-qs-text hover:bg-qs-red hover:text-white hover:border-qs-red transition-colors"
                  >
                    <b.icon size={15} /> {b.label}
                  </a>
                ))}
              </div>

              <div className="mt-7 overflow-hidden border border-qs-border">
                <iframe
                  title="Google Maps - Pizzeria Quattro Stelle"
                  src={mapsEmbedUrl}
                  width="100%"
                  height="260"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="google-map"
                />
              </div>
            </div>
          </Reveal>

          {/* Right: form or vacation notice */}
          <Reveal delay={120}>
            {vacActive ? (
              <div
                data-testid="reservation-vacation-notice"
                className="bg-qs-bg2 p-7 md:p-9 h-full flex flex-col items-center justify-center text-center min-h-[420px]"
              >
                <div className="w-16 h-16 rounded-full bg-qs-gold/15 flex items-center justify-center mb-5">
                  <Palmtree className="text-qs-gold" size={30} strokeWidth={1.4} />
                </div>
                <h3 className="font-cormorant text-2xl md:text-3xl text-qs-text mb-3">
                  {lang === "en" ? "Reservations paused" : "Reservierungen pausiert"}
                </h3>
                <div className="gold-divider my-2" />
                <p className="font-outfit text-qs-text2 max-w-sm mt-2 leading-relaxed">{vacMsg}</p>
              </div>
            ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="reservation-form"
              className="bg-qs-bg2 p-7 md:p-9"
            >
              <h3 className="font-cormorant text-2xl text-qs-text mb-1">{f.title}</h3>
              <div className="gold-divider my-4" />

              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.name}</label>
                  <input data-testid="res-name" required value={form.name} onChange={update("name")} placeholder={f.namePh} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">E-Mail</label>
                    <input data-testid="res-email" type="email" required value={form.email} onChange={update("email")} placeholder={f.emailPh} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.phone}</label>
                    <input data-testid="res-phone" required value={form.phone} onChange={update("phone")} placeholder={f.phonePh} className={inputCls} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.date}</label>
                    <input data-testid="res-date" type="date" required value={form.date} onChange={update("date")} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.time}</label>
                    <select data-testid="res-time" required value={form.time} onChange={update("time")} className={inputCls}>
                      <option value="">--:--</option>
                      {TIME_SLOTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.guests}</label>
                    <select data-testid="res-guests" value={form.guests} onChange={update("guests")} className={inputCls}>
                      {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{f.message}</label>
                  <textarea data-testid="res-message" rows={3} value={form.message} onChange={update("message")} placeholder={f.messagePh} className={inputCls} />
                </div>
              </div>

              <button
                data-testid="reservation-submit-button"
                type="submit"
                disabled={submitting || done}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-qs-red text-white hover:bg-qs-reddark transition-colors rounded-full py-4 text-xs font-semibold tracking-[0.14em] uppercase disabled:opacity-70"
              >
                {done ? (
                  <><Check size={16} /> {f.success.split("!")[0]}!</>
                ) : submitting ? (
                  f.submitting
                ) : (
                  <><Send size={15} /> {f.submit}</>
                )}
              </button>
            </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
